import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc, getDoc, collection, query, where, onSnapshot, addDoc, Timestamp, updateDoc, deleteDoc, getDocs } from 'firebase/firestore';
import { IoArrowBack, IoLockClosedOutline, IoClose, IoPeople, IoTime, IoCalendar, IoSwapHorizontal, IoAlertCircleOutline } from 'react-icons/io5';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import { useAuth } from '../context/AuthContext';
import './DetalleAmenidadScreen.css'; // --- Premium CSS ---
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

moment.locale('es');
const localizer = momentLocalizer(moment);

// --- Modal ---
const ReservaModal = ({ reserva, amenidad, onClose, onModifyAttendees, onCancel, onChangeHorario, isReadOnly }) => {
  const checkAnticipacion = () => {
    const { horasAnticipacion } = amenidad.reglas;
    const ahora = new Date();
    const horaDeReserva = reserva.start;
    const milisegundosDeDiferencia = horaDeReserva.getTime() - ahora.getTime();
    const horasDeDiferencia = milisegundosDeDiferencia / (1000 * 60 * 60);
    return horasDeDiferencia >= horasAnticipacion;
  };
  const puedeModificar = checkAnticipacion();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-top-close-button" onClick={onClose}><IoClose /></button>

        <h2 style={{ margin: '0 0 20px 0', fontSize: '1.5rem', color: '#111827' }}>Detalle de Reservación</h2>

        <div className="modal-info-grid">
          {/* Using premium structure */}
          <h3>{amenidad.nombre}</h3>
          {reserva.nombreResidente && (
            <span style={{ fontWeight: '600', color: '#4f46e5', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IoPeople size={18} /> Reservado por: {reserva.nombreResidente}
            </span>
          )}
          <span><IoCalendar size={18} /> {moment(reserva.start).format('dddd, D [de] MMMM')}</span>
          <span><IoTime size={18} /> {moment(reserva.start).format('h:mm A')} - {moment(reserva.end).format('h:mm A')}</span>
          <span><IoPeople size={18} /> {reserva.numeroUsuarios} asistentes</span>
        </div>

        {isReadOnly ? (
          <div style={{ marginTop: '20px', padding: '12px', background: '#f3f4f6', borderRadius: '8px', color: '#4b5563', fontSize: '0.9rem', textAlign: 'center', fontWeight: '500' }}>
            Modo Consulta (Vigilancia). No tienes permisos para modificar o cancelar esta reservación.
          </div>
        ) : puedeModificar ? (
          <div className="modal-actions">
            <button className="btn-modal btn-primary" onClick={onModifyAttendees}>
              Modificar Asistentes
            </button>
            <button className="btn-modal btn-warning" onClick={onChangeHorario}>
              <IoSwapHorizontal /> Cambiar Horario
            </button>
            <button className="btn-modal btn-danger" onClick={onCancel}>
              Cancelar Reservación
            </button>
          </div>
        ) : (
          <div className="form-mensaje error" style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <IoAlertCircleOutline size={24} />
            <span style={{ fontSize: '0.9rem' }}>No es posible modificar o cancelar (se requiere {amenidad.reglas.horasAnticipacion}h de anticipación).</span>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Modales Reutilizables ---
const ActionConfirmModal = ({ visible, title, message, type, onConfirm, onClose }) => {
  if (!visible) return null;
  const isError = type === 'error';
  const isConfirm = type === 'confirm';

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 10000 }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px', textAlign: 'center' }}>
        <button className="modal-top-close-button" onClick={onClose}><IoClose /></button>
        <div style={{ marginBottom: '15px', color: isError ? '#ef4444' : '#6366f1' }}>
          <IoAlertCircleOutline size={48} />
        </div>
        <h2 style={{ fontSize: '1.2rem', marginBottom: '10px', color: '#1e293b' }}>{title}</h2>
        <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '25px', lineHeight: '1.5' }}>{message}</p>
        
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          {isConfirm && (
            <button className="btn-modal btn-secondary" onClick={onClose} style={{ flex: 1, padding: '10px' }}>
              Cancelar
            </button>
          )}
          <button 
            className={`btn-modal ${isError || (isConfirm && title.includes('Cancelar')) ? 'btn-danger' : 'btn-primary'}`} 
            onClick={() => { if (onConfirm) onConfirm(); onClose(); }} 
            style={{ flex: 1, padding: '10px' }}
          >
            {isConfirm ? 'Confirmar' : 'Entendido'}
          </button>
        </div>
      </div>
    </div>
  );
};

const FormAsistentesModal = ({ visible, title, limit, initialValue, onConfirm, onClose }) => {
  const [count, setCount] = useState(initialValue || 1);

  useEffect(() => {
    if (visible) setCount(initialValue || 1);
  }, [visible, initialValue]);

  if (!visible) return null;

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 10000 }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '350px', textAlign: 'center' }}>
        <button className="modal-top-close-button" onClick={onClose}><IoClose /></button>
        <div style={{ marginBottom: '15px', color: '#6366f1' }}>
          <IoPeople size={48} />
        </div>
        <h2 style={{ fontSize: '1.2rem', marginBottom: '10px', color: '#1e293b' }}>{title}</h2>
        <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '20px' }}>Máximo permitido: {limit} personas</p>
        
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginBottom: '30px' }}>
          <button 
            type="button" 
            onClick={() => setCount(Math.max(1, count - 1))}
            style={{ width: '40px', height: '40px', borderRadius: '50%', border: '1px solid #cbd5e1', background: '#f8fafc', fontSize: '1.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}
          >-</button>
          <span style={{ fontSize: '2rem', fontWeight: 'bold', width: '50px', color: '#1e293b' }}>{count}</span>
          <button 
            type="button" 
            onClick={() => setCount(Math.min(limit, count + 1))}
            style={{ width: '40px', height: '40px', borderRadius: '50%', border: '1px solid #cbd5e1', background: '#f8fafc', fontSize: '1.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}
          >+</button>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-modal btn-secondary" onClick={onClose} style={{ flex: 1, padding: '10px' }}>
            Cancelar
          </button>
          <button className="btn-modal btn-primary" onClick={() => { onConfirm(count); onClose(); }} style={{ flex: 1, padding: '10px' }}>
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Main Screen ---
function DetalleAmenidadScreen() {
  const navigate = useNavigate();
  const { amenidadId } = useParams();
  const { currentUser, userData, isMoroso } = useAuth();

  const [amenidad, setAmenidad] = useState(null);
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('week');
  const [selectedReserva, setSelectedReserva] = useState(null);
  const [modifyingReserva, setModifyingReserva] = useState(null);
  const [showRules, setShowRules] = useState(false);

  // --- Modal States ---
  const [actionModal, setActionModal] = useState({ visible: false, title: '', message: '', type: 'info', onConfirm: null });
  const [asistentesModal, setAsistentesModal] = useState({ visible: false, title: '', limit: 1, initialValue: 1, onConfirm: null });

  const closeActionModal = () => setActionModal(prev => ({ ...prev, visible: false }));
  const closeAsistentesModal = () => setAsistentesModal(prev => ({ ...prev, visible: false }));
  
  const showAlert = (title, message, type = 'error', onConfirm = null) => {
    setActionModal({ visible: true, title, message, type, onConfirm });
  };

  // --- Data Loading ---
  useEffect(() => {
    if (isMoroso) {
      setError("Acceso denegado. Tienes adeudos pendientes.");
      setLoading(false);
      return;
    }
    if (userData?.bloqueoAdministrativo) {
      setError("Tu cuenta está bloqueada por la administración.");
      setLoading(false); return;
    }
    if (!amenidadId) return;

    const cargarDatosAmenidad = async () => {
      const docRef = doc(db, "amenidades", amenidadId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.bloqueadaPorAdmin === true) {
          setError("Esta amenidad se encuentra en mantenimiento.");
          setLoading(false);
          return;
        }

        // --- Validación de Acceso Exclusivo (Etiquetas) ---
        const userTags = userData?.etiquetasAcceso || [];
        const tagsAmenidad = data.derechosAcceso || [];
        const viejaTags = data.derechoAcceso || [];
        let tagsReales = [];
        
        if (tagsAmenidad.length > 0) {
            tagsReales = tagsAmenidad;
        } else if (Array.isArray(viejaTags)) {
            tagsReales = viejaTags;
        } else if (typeof viejaTags === 'string') {
            tagsReales = viejaTags.split(',').map(t => t.trim());
        }

        let tieneAcceso = false;
        if (tagsReales.length === 0) {
            tieneAcceso = true;
        } else if (tagsReales.some(t => typeof t === 'string' && t.toLowerCase() === 'todos')) {
            tieneAcceso = true;
        } else {
            tieneAcceso = tagsReales.some(tag => {
                const tagLower = typeof tag === 'string' ? tag.toLowerCase().trim() : '';
                return userTags.some(uTag => typeof uTag === 'string' && uTag.toLowerCase().trim() === tagLower);
            });
        }

        if (!tieneAcceso) {
          setError("Acceso Restringido. Esta amenidad es exclusiva para grupos autorizados a los que no perteneces.");
          setLoading(false);
          return;
        }
        // -------------------------------------------------

        setAmenidad({ ...data, id: docSnap.id });
      } else {
        setError("No se encontró la amenidad.");
        setLoading(false);
      }
    };
    cargarDatosAmenidad();

    const q = query(
      collection(db, "reservaciones"),
      where("amenidadId", "==", amenidadId)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const listaReservas = snapshot.docs.map(doc => {
        const data = doc.data();
        const esStaffConsultante = userData?.rol === 'vigilante' || userData?.rol === 'admin' || userData?.rol === 'comite';
        return {
          ...data, id: doc.id,
          title: esStaffConsultante 
            ? `${data.nombreResidente || 'Reservado'}` 
            : (data.residenteId === currentUser?.uid ? 'Tu Reserva' : 'Reservado'),
          start: data.fechaInicio.toDate(), end: data.fechaFin.toDate(),
          residenteId: data.residenteId, // Keep for check
          nombreResidente: data.nombreResidente
        };
      });
      setReservas(listaReservas);
      setLoading(false);
    }, (err) => {
      console.error("Error cargando reservaciones: ", err);
      setError("Error al cargar las reservaciones.");
      setLoading(false);
    });
    return () => unsubscribe();
  }, [amenidadId, userData, isMoroso, currentUser]);

  // --- Logic Functions (Validations) ---
  const validarSlot = (start, end) => {
    if (error) { showAlert("Error", error); return false; }
    if (new Date(start) < new Date()) {
      showAlert("Horario Pasado", "No puedes reservar en el pasado."); return false;
    }
    const { horasAnticipacion } = amenidad.reglas;
    const ahora = new Date();
    const horaDeReserva = new Date(start);
    const milisegundosDeDiferencia = horaDeReserva.getTime() - ahora.getTime();
    const horasDeDiferencia = milisegundosDeDiferencia / (1000 * 60 * 60);
    if (horasDeDiferencia < horasAnticipacion) {
      showAlert("Anticipación Requerida", `Debes reservar con al menos ${horasAnticipacion} horas de anticipación.`);
      return false;
    }
    return true;
  };

  const handleSelectSlot = async ({ start, end }) => {
    if (!currentUser || !userData) {
      showAlert("Error", "Error de usuario. Recarga la página.");
      return;
    }
    if (userData?.rol === 'vigilante') {
      showAlert("Operación No Permitida", "Los vigilantes no tienen permisos para agendar reservaciones.");
      return;
    }
    if (!validarSlot(start, end)) return;

    // Simultaneous Capacity Check (En Memoria)
    const { capacidadSimultanea } = amenidad.reglas;
    if (capacidadSimultanea && capacidadSimultanea > 0) {
      const traslapes = reservas.filter(r => 
        (start < r.end && end > r.start) && 
        (modifyingReserva ? r.id !== modifyingReserva.id : true)
      );
      if (traslapes.length >= capacidadSimultanea) {
        showAlert("Aforo Lleno", `Ya hay ${traslapes.length} reservaciones en este horario. Capacidad máxima simultánea es de ${capacidadSimultanea}.`);
        return;
      }
    }

    // Frequency Check (En Memoria)
    if (!modifyingReserva) {
      const { limiteReservas, limitePeriodoNum, limitePeriodoTipo } = amenidad.reglas;
      if (limiteReservas && limiteReservas > 0) {
        const momentoBase = moment(start);
        let inicioPeriodo, finPeriodo;
        switch ((limitePeriodoTipo || '').toLowerCase()) {
          case 'semana': case 'semanas':
            inicioPeriodo = momentoBase.clone().startOf('isoWeek').toDate();
            finPeriodo = momentoBase.clone().endOf('isoWeek').toDate();
            break;
          case 'mes': case 'meses':
            inicioPeriodo = momentoBase.clone().startOf('month').toDate();
            finPeriodo = momentoBase.clone().endOf('month').toDate();
            break;
          default:
            inicioPeriodo = momentoBase.clone().startOf('day').toDate();
            finPeriodo = momentoBase.clone().endOf('day').toDate();
            break;
        }

        const misReservasEnPeriodo = reservas.filter(r => 
          r.residenteId === currentUser.uid &&
          r.start >= inicioPeriodo &&
          r.start <= finPeriodo
        );

        if (misReservasEnPeriodo.length >= limiteReservas) {
          showAlert("Límite Alcanzado", `Has alcanzado tu límite de ${limiteReservas} reservas por ${limitePeriodoTipo}.`);
          return;
        }
      }
    }

    // Modification Flow
    if (modifyingReserva) {
      showAlert(
        "Confirmar Reprogramación",
        `¿Confirmas el cambio al ${moment(start).format('DD/MM/YYYY')} a las ${moment(start).format('h:mm A')}?`,
        "confirm",
        async () => {
          try {
            await updateDoc(doc(db, "reservaciones", modifyingReserva.id), {
              fechaInicio: Timestamp.fromDate(new Date(start)),
              fechaFin: Timestamp.fromDate(new Date(end))
            });
            showAlert("Éxito", "El horario ha sido actualizado exitosamente.", "info");
          } catch (e) {
            console.error(e);
            showAlert("Error", "Ocurrió un error al actualizar el horario.");
          } finally {
            setModifyingReserva(null);
          }
        }
      );
      return;
    }

    // Creation Flow
    const { limiteUsuariosPorReserva } = amenidad.reglas;
    setAsistentesModal({
      visible: true,
      title: "Nueva Reservación",
      limit: limiteUsuariosPorReserva,
      initialValue: 1,
      onConfirm: async (numUsuarios) => {
        try {
          await addDoc(collection(db, "reservaciones"), {
            amenidadId: amenidad.id,
            condominioId: amenidad.condominioId || userData?.condominioId || 'SOMOS_DEFAULT',
            residenteId: currentUser.uid,
            nombreResidente: userData.nombre,
            fechaInicio: Timestamp.fromDate(new Date(start)),
            fechaFin: Timestamp.fromDate(new Date(end)),
            estatus: "Confirmada", 
            numeroUsuarios: numUsuarios
          });
          showAlert("¡Reservado exitosamente!", `Tu espacio para ${numUsuarios} personas ha sido confirmado.`, "info");
        } catch (e) { 
          console.error(e); 
          showAlert("Error", "No se pudo completar la reservación.");
        }
      }
    });
  };

  const handleSelectEvent = (event) => {
    if (!currentUser) return;
    if (userData?.rol === 'vigilante') {
      setSelectedReserva(event);
      return;
    }
    if (event.residenteId !== currentUser.uid) {
      showAlert("No Disponible", "Este horario está ocupado por otro residente."); 
      return;
    }
    if (new Date(event.start) < new Date()) {
      showAlert("Reservación Pasada", "Este evento ya ha concluido."); 
      return;
    }
    setSelectedReserva(event);
  };

  const handleCancelReserva = async () => {
    if (selectedReserva) {
      showAlert(
        "Cancelar Reservación",
        "¿Estás seguro de que deseas cancelar de manera definitiva esta reservación?",
        "confirm",
        async () => {
          try {
            await deleteDoc(doc(db, "reservaciones", selectedReserva.id));
            showAlert("Cancelada", "La reservación ha sido eliminada.", "info");
            setSelectedReserva(null);
          } catch (e) {
            showAlert("Error", "Ocurrió un problema al cancelar.");
          }
        }
      );
    }
  };

  const handleModifyReserva = () => {
    if (!selectedReserva) return;
    const { limiteUsuariosPorReserva } = amenidad.reglas;
    
    setAsistentesModal({
      visible: true,
      title: "Modificar Asistentes",
      limit: limiteUsuariosPorReserva,
      initialValue: selectedReserva.numeroUsuarios || 1,
      onConfirm: async (numUsuarios) => {
        try {
          await updateDoc(doc(db, "reservaciones", selectedReserva.id), { numeroUsuarios: numUsuarios });
          showAlert("Actualizado", "El número de asistentes ha sido actualizado.", "info");
          setSelectedReserva(null);
        } catch (e) {
          showAlert("Error", "No se pudo actualizar los asistentes.");
        }
      }
    });
  };

  const handleChangeHorario = () => {
    if (!selectedReserva) return;
    setModifyingReserva(selectedReserva);
    setSelectedReserva(null);
    showAlert("Modo Reprogramación", "Selecciona el nuevo bloque de tiempo disponible directamente en el calendario.", "info");
  };

  const messages = { today: 'Hoy', previous: '<', next: '>', month: 'Mes', week: 'Semana', day: 'Día', agenda: 'Agenda' };
  const visibleReservas = useMemo(() => modifyingReserva ? reservas.filter(r => r.id !== modifyingReserva.id) : reservas, [reservas, modifyingReserva]);

  if (loading) {
    return (
      <div className="ks-page">
        <KondoSidebar activeRoute="/reservar" />

        <main className="ks-main">
          <KondoBrandHeader showBack={false} />

          <div className="ks-scroll-area">
            <div className="detalle-container" style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
              <p style={{ color: '#64748b', fontSize: '1.1rem' }}>Cargando...</p>
            </div>
          </div>
        </main>

        <KondoBottomNav activeRoute="/reservar" />
      </div>
    );
  }

  if (!amenidad && !error) {
    return (
      <div className="ks-page">
        <KondoSidebar activeRoute="/reservar" />

        <main className="ks-main">
          <KondoBrandHeader showBack={false} />

          <div className="ks-scroll-area">
            <div className="detalle-container" style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
              <p style={{ color: '#64748b', fontSize: '1.1rem' }}>Cargando información...</p>
            </div>
          </div>
        </main>

        <KondoBottomNav activeRoute="/reservar" />
      </div>
    );
  }

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/reservar" />

      <main className="ks-main">
        <KondoBrandHeader showBack={false} />

        <div className="ks-scroll-area">
          <div className="detalle-container">
            {/* Sticky Compact Header */}
            <div className="detalle-fixed-wrapper">
              <div className="detalle-banner-compact">
                <button onClick={() => navigate('/reservar')} className="detalle-back-btn glass">
                  <IoArrowBack size={24} />
                </button>

                <div className="detalle-banner-content">
                  <h2>{amenidad ? amenidad.nombre : 'Cargando...'}</h2>
                  <p>Detalles y Reservas</p>
                </div>

                <div className="detalle-banner-icon">
                  <IoCalendar />
                </div>
              </div>
            </div>

            {error ? (
              <div className="bloqueo-overlay">
                <div className="icon"><IoLockClosedOutline /></div>
                <h2>Acceso Restringido</h2>
                <p>{error}</p>
              </div>
            ) : (
              (() => {
                const minTime = moment(amenidad.reglas.horarioApertura, "HH:mm").toDate();
                const maxTime = moment(amenidad.reglas.horarioCierre, "HH:mm").toDate();
                return (
                  <>
                    <div className="amenidad-info-card">
                      <p className="amenidad-desc-text">{amenidad.descripcion}</p>
                      <div className="amenidad-rules-box">
                        <div
                          className="amenidad-rules-header"
                          onClick={() => setShowRules(!showRules)}
                        >
                          <div className="amenidad-rules-title">
                            <IoAlertCircleOutline /> Reglas de Uso
                          </div>
                          <span className="rules-toggle-icon">
                            {showRules ? 'Ocultar' : 'Ver reglas'}
                          </span>
                        </div>

                        {showRules && (
                          <ul className="amenidad-rules-list open">
                            <li>Máximo {amenidad.reglas.limiteUsuariosPorReserva} personas</li>
                            <li>Anticipación: {amenidad.reglas.horasAnticipacion} horas</li>
                            <li>Límite: {amenidad.reglas.limiteReservas} reservas cada {amenidad.reglas.limitePeriodoNum} {amenidad.reglas.limitePeriodoTipo}(s)</li>
                            {amenidad.reglas.capacidadSimultanea && (
                              <li>Aforo Simultáneo: {amenidad.reglas.capacidadSimultanea} reservas</li>
                            )}
                          </ul>
                        )}
                      </div>
                    </div>

                    {modifyingReserva && (
                      <div className="editing-alert">
                        <IoAlertCircleOutline size={24} />
                        <strong>Modo Reprogramación:</strong> Selecciona el nuevo horario en el calendario.
                      </div>
                    )}

                    <div className="calendar-wrapper">
                      <Calendar
                        localizer={localizer}
                        events={visibleReservas}
                        startAccessor="start"
                        endAccessor="end"
                        defaultView={view}
                        views={['month', 'week', 'day', 'agenda']}
                        selectable={!error}
                        longPressThreshold={10} // Critical for mobile touch interaction
                        onSelectSlot={handleSelectSlot}
                        messages={messages}
                        date={date}
                        view={view}
                        onNavigate={(d) => { setDate(d); setModifyingReserva(null); }}
                        onView={(v) => { setView(v); setModifyingReserva(null); }}
                        min={minTime}
                        max={maxTime}
                        step={amenidad.reglas.duracionBloque || 60}
                        timeslots={1}
                        onSelectEvent={handleSelectEvent}
                      />
                    </div>
                  </>
                )
              })()
            )}

            {selectedReserva && (
              <ReservaModal
                reserva={selectedReserva}
                amenidad={amenidad}
                onClose={() => setSelectedReserva(null)}
                onModifyAttendees={handleModifyReserva}
                onCancel={handleCancelReserva}
                onChangeHorario={handleChangeHorario}
                isReadOnly={userData?.rol === 'vigilante'}
              />
            )}

            {/* Global Application Modals */}
            <ActionConfirmModal 
              {...actionModal} 
              onClose={closeActionModal} 
            />
            
            <FormAsistentesModal 
              {...asistentesModal} 
              onClose={closeAsistentesModal} 
            />
          </div>
        </div>
      </main>

      <KondoBottomNav activeRoute="/reservar" />
    </div>
  );
}

export default DetalleAmenidadScreen;